import { useState } from "react";
import { X, Printer, Eye, Pencil, Save } from "lucide-react";
import { ScoreBadge, openPrintWindow } from "features/cv-processor/CvProcessorPage";
import { buildCoverLetterPrintHtml } from "features/cover-letter/CoverLetterPage";
import { createCoverLetterDocument, createEmployeeCoverLetterDocumentAsAdmin } from "features/cover-letter/api/CoverLetterApi";
import SaveAsNewCoverLetterModal from "features/cover-letter/ui/SaveAsNewCoverLetterModal";

interface Props {
  enhancedContent: string;
  overallScore?: number | null;
  critique?: string | null;
  topPriorityFix?: string | null;
  jobTitle: string;
  companyName?: string | null;
  isAdmin?: boolean;
  /** Set when isAdmin and admin ran this enhancement on behalf of a Technical
   * Manager — required for "Save as new Cover Letter" to land in that
   * employee's own collection instead of the admin's. */
  employeeCode?: string | null;
  onClose: () => void;
}

/** Shared "View Enhanced Cover Letter" modal — used identically by the LinkedIn
 * Email Alerts and TheirStack (Other Jobs) pipelines, mirroring EnhancedCvModal's
 * "do not fork per pipeline" convention. Edits here are local-only preview edits,
 * same as EnhancedCvModal — they're never persisted back to the saved document. */
export default function EnhancedCoverLetterModal({
  enhancedContent, overallScore, critique, topPriorityFix, jobTitle, companyName, isAdmin, employeeCode, onClose,
}: Props) {
  const [activeView, setActiveView] = useState<"edit" | "preview">("preview");
  const [localContent, setLocalContent] = useState(enhancedContent);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  function handlePrint() {
    openPrintWindow(buildCoverLetterPrintHtml({
      id: 0, owner_type: "", owner_id: 0, title: jobTitle, content: localContent,
      category_id: null, last_score: null, last_enhanced_at: null, created_at: "", updated_at: "",
    }));
  }

  async function handleSaveAsNew(title: string, categoryId: number) {
    if (isAdmin && !employeeCode) {
      throw new Error("Could not determine which Technical Manager owns this cover letter. Please reselect them and try again.");
    }
    const payload = { title, category_id: categoryId, content: localContent };
    if (isAdmin && employeeCode) {
      await createEmployeeCoverLetterDocumentAsAdmin({ ...payload, employee_code: employeeCode });
    } else {
      await createCoverLetterDocument(payload);
    }
    setShowSaveModal(false);
    setSaveMessage("Saved as a new cover letter — find it under My Cover Letters.");
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[92vh] overflow-hidden bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground font-inter">Enhanced Cover Letter Preview</h2>
              {overallScore != null && <ScoreBadge score={overallScore} />}
            </div>
            <p className="text-xs text-muted-foreground font-inter mt-0.5 truncate">
              Enhanced for: {jobTitle}{companyName ? ` at ${companyName}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <button
              onClick={() => setShowSaveModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-muted hover:bg-surface-muted/70 border border-border text-foreground text-xs font-inter font-medium rounded-lg transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              Save as Regular Cover Letter
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {saveMessage && (
          <div className="px-6 py-2 border-b border-border bg-emerald-500/10 shrink-0">
            <p className="text-xs text-emerald-400 font-inter">{saveMessage}</p>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-1 p-1 bg-surface-muted/60 rounded-xl w-fit border border-border">
            <button
              onClick={() => setActiveView("edit")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                activeView === "edit" ? "bg-surface text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              onClick={() => setActiveView("preview")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                activeView === "preview" ? "bg-surface text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              Preview
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
          {(critique && critique !== "none") || (topPriorityFix && topPriorityFix !== "none") ? (
            <div className="mb-4 px-4 py-3 rounded-xl bg-surface-muted/40 border border-border space-y-1">
              {critique && critique !== "none" && (
                <p className="text-xs text-muted-foreground font-inter">
                  <span className="text-muted-foreground">Critique: </span>{critique}
                </p>
              )}
              {topPriorityFix && topPriorityFix !== "none" && (
                <p className="text-xs text-muted-foreground font-inter">
                  <span className="text-muted-foreground">Top fix: </span>{topPriorityFix}
                </p>
              )}
            </div>
          ) : null}

          {activeView === "preview" ? (
            <div className="rounded-xl bg-white p-8 shadow-sm max-w-2xl mx-auto" style={{ fontFamily: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif' }}>
              {localContent.split(/\n\s*\n/).map((p, i) => (
                <p key={i} className="text-sm leading-7 text-zinc-800 mb-4 whitespace-pre-wrap">{p.trim()}</p>
              ))}
            </div>
          ) : (
            <textarea
              value={localContent}
              onChange={(e) => setLocalContent(e.target.value)}
              rows={16}
              className="w-full px-4 py-3 bg-surface-muted/50 border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 resize-none"
            />
          )}
        </div>
      </div>
      {showSaveModal && (
        <SaveAsNewCoverLetterModal
          defaultTitle={`${jobTitle}${companyName ? ` @ ${companyName}` : ""} — Cover Letter`}
          onSave={handleSaveAsNew}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}
